import { useEffect, useState } from 'react';
import { Cpu, LayoutTemplate } from 'lucide-react';
import type {
  LayoutReaderHeldBackScore,
  LayoutReaderLearningState,
  LayoutReaderModelVersion,
  LayoutReaderRunState,
  LayoutReaderTypeState,
} from '../shared/desktop-api';
import './layout-reader-learning.css';

/**
 * The layout reader's learning for one job (ADR-0086): one row per document type with the
 * model reading it, its held-back right and wrong answers as a small bar, and when the next
 * attempt runs. Every number is the engine's (`layout-reader-models`).
 */
export function LayoutReaderLearning({ jobId }: { jobId: string }) {
  const state = useLayoutReaderModels(jobId);
  if (!state || !state.readerEnabled || state.types.length === 0) return null;
  const toNext = Math.max(0, state.nextAttemptAt - state.confirmedDocuments);
  const progress =
    state.nextAttemptAt > 0 ? Math.min(1, state.confirmedDocuments / state.nextAttemptAt) : 0;
  return (
    <section className="lr-learning" aria-label="Layout reader">
      <header className="lr-head">
        <LayoutTemplate size={17} aria-hidden="true" />
        <h3>Layout reader</h3>
        {state.running ? (
          <span className="lr-next is-running" role="status">
            <span className="lr-next-bar" aria-hidden="true">
              <i style={{ width: `${Math.round(runFraction(state.running) * 100)}%` }} />
            </span>
            {runLabel(state.running)}
          </span>
        ) : state.canLearnHere ? (
          <span className="lr-next">
            <span className="lr-next-bar" aria-hidden="true">
              <i style={{ width: `${Math.round(progress * 100)}%` }} />
            </span>
            {toNext === 0
              ? 'Next attempt when idle'
              : `Next attempt at ${state.nextAttemptAt.toLocaleString()} confirmed`}
          </span>
        ) : (
          <span className="lr-next is-off">
            <Cpu size={14} aria-hidden="true" />
            Can’t learn on this computer
          </span>
        )}
      </header>
      {/* A learner that can't learn here says why, in view. */}
      {!state.running && !state.canLearnHere && state.cannotLearnReason && (
        <p className="lr-reason">{state.cannotLearnReason}</p>
      )}
      <ul className="lr-rows">
        {state.types.map((type) => (
          <LayoutReaderRow key={type.documentType} type={type} />
        ))}
      </ul>
    </section>
  );
}

/** How far the running attempt is, 0 to 1: training steps, then held-back documents read. */
export function runFraction(run: LayoutReaderRunState): number {
  if (run.phase === 'judging') return run.toJudge > 0 ? Math.min(1, run.judged / run.toJudge) : 0;
  return run.steps > 0 ? Math.min(1, run.step / run.steps) : 0;
}

/** The running attempt in a few words: what it is doing and how far along it is. */
export function runLabel(run: LayoutReaderRunState): string {
  switch (run.phase) {
    case 'training':
      return run.steps > 0
        ? `Learning: step ${run.step.toLocaleString()} of ${run.steps.toLocaleString()}`
        : 'Learning: starting';
    case 'judging':
      return `Checking: ${run.judged.toLocaleString()} of ${run.toJudge.toLocaleString()} held back`;
    case 'waiting':
      return 'Learning paused while you work';
    default:
      return 'Learning: getting ready';
  }
}

/** The fine-tune reading this type, or null while the base model does. */
function tunedModel(type: LayoutReaderTypeState): LayoutReaderModelVersion | null {
  return type.model === 'fine-tuned' && type.inForce ? type.inForce : null;
}

/**
 * The model chip: "Base", or "Tuned vN" where N counts the type's adopted models up to the one
 * in force (one earlier fine-tune makes it v2; a rollback takes the line back one). The engine's
 * `version` names the model's folder (a hash prefix for a carried model, a timestamp for a
 * learned one) and is never shown.
 */
export function modelLabel(type: LayoutReaderTypeState): string {
  return tunedModel(type) ? `Tuned v${type.earlier.length + 1}` : 'Base';
}

function LayoutReaderRow({ type }: { type: LayoutReaderTypeState }) {
  const tuned = tunedModel(type);
  const score: LayoutReaderHeldBackScore | null = tuned
    ? tuned.after
    : (type.lastVerdict?.inForce ?? null);
  const before = tuned ? tuned.before : null;
  const verdict = type.lastVerdict;
  return (
    <li className="lr-row">
      <strong>{type.documentType}</strong>
      <span className={`lr-model${tuned ? ' is-tuned' : ''}`}>{modelLabel(type)}</span>
      {score ? (
        <HeldBackBar score={score} before={before} />
      ) : (
        <span className="lr-none">Not judged yet</span>
      )}
      <em className={verdict && !verdict.adopted ? 'is-held' : undefined}>
        {!verdict ? '' : verdict.adopted ? 'Adopted' : 'Held'}
      </em>
      {/* A held model says why, in view: a refusal to adopt is a result worth showing. */}
      {verdict && !verdict.adopted && verdict.reason && (
        <small className="lr-reason">Held: {verdict.reason}</small>
      )}
    </li>
  );
}

/** Right answers left, wrong answers right, on held-back documents; the earlier model's as a faint mark. */
function HeldBackBar({
  score,
  before,
}: {
  score: LayoutReaderHeldBackScore;
  before: LayoutReaderHeldBackScore | null;
}) {
  const total = Math.max(1, score.right + score.wrong + score.declined);
  return (
    <span
      className="lr-bar"
      role="img"
      aria-label={`${score.right} right, ${score.wrong} wrong on held-back documents${before ? `, against ${before.right} and ${before.wrong} before` : ''}`}
    >
      <span className="lr-bar-track" aria-hidden="true">
        <i className="is-right" style={{ width: `${(score.right / total) * 100}%` }} />
        <i className="is-wrong" style={{ width: `${(score.wrong / total) * 100}%` }} />
      </span>
      <small aria-hidden="true">
        <b>{score.right.toLocaleString()}</b> ✓{' '}
        <b className="is-wrong">{score.wrong.toLocaleString()}</b> ✗
      </small>
    </span>
  );
}

const RUNNING_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;
function useLayoutReaderModels(jobId: string): LayoutReaderLearningState | null {
  const [state, setState] = useState<LayoutReaderLearningState | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    setState(null);
    if (!jobId || !api?.layoutReaderModels) return;
    let live = true;
    // Unavailable is quiet: this section is extra evidence, not the Learning screen's footing.
    let timer: ReturnType<typeof setTimeout> | undefined;
    let last: LayoutReaderLearningState | null = null;
    // A running attempt is read again every few seconds so its progress moves. While this computer can
    // learn, an attempt may start (or a model be adopted) when it next sits idle, so it is read slowly too.
    const again = (known: LayoutReaderLearningState | null) => {
      if (known?.running) timer = setTimeout(read, RUNNING_POLL_MS);
      else if (!known || (known.readerEnabled && known.canLearnHere))
        timer = setTimeout(read, IDLE_POLL_MS);
    };
    const read = () =>
      void api.layoutReaderModels!(jobId)
        .then((next) => {
          if (!live || next?.jobId !== jobId) return;
          last = next;
          setState(next);
          again(next);
        })
        .catch(() => {
          // A failed read keeps what was last shown and tries again; it never stops the polling for good.
          if (live) again(last);
        });
    read();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);
  return state;
}
